import { type Constructor } from "@anna-money/anna-web-lib/utils";
import { serializerWrapper } from "helpers/serialization";
import { FallbackStorage } from "./fallbackStorage";

const annaPrefix = "anna_";

type BuiltInConstructor = StringConstructor | NumberConstructor | BooleanConstructor | ObjectConstructor;

interface StorageLike {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
  getKeys: () => string[];
}

class BrowserStorage implements StorageLike {
  getItem(key: string): string | null {
    return localStorage.getItem(key);
  }

  setItem(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  removeItem(key: string): void {
    localStorage.removeItem(key);
  }

  getKeys(): string[] {
    const keys: string[] = [];
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (key !== null) {
        keys.push(key);
      }
    }

    return keys;
  }
}

const createDefaultStorage = (): StorageLike => {
  try {
    localStorage.getItem(annaPrefix);
    return new BrowserStorage();
  } catch (e) {
    return new FallbackStorage();
  }
};

export class DataStorage {
  private readonly _storage: StorageLike;

  constructor(storage: StorageLike = createDefaultStorage()) {
    this._storage = storage;
  }

  private _getKey(key: string): string {
    return `${annaPrefix}${key}`;
  }

  get(key: string, type: StringConstructor): string | null;
  get(key: string, type: NumberConstructor): number | null;
  get(key: string, type: BooleanConstructor): boolean | null;
  get<T>(key: string, type: ObjectConstructor): T | null;
  get<T>(key: string, type: Constructor<T>): T | null;
  get<T>(key: string, type: Constructor<T> | BuiltInConstructor): string | number | boolean | T | null {
    const item = this._storage.getItem(this._getKey(key));

    if (item === null) {
      return null;
    }

    if (type === String) {
      return item;
    }

    if (type === Number || type === Boolean || type === Object) {
      return JSON.parse(item);
    }

    return serializerWrapper.deserialize(JSON.parse(item), type as Constructor<T>);
  }

  set<T>(key: string, data: T): void {
    const item = serializerWrapper.serialize(data);
    this._storage.setItem(this._getKey(key), typeof item === "string" ? item : JSON.stringify(item));
  }

  remove(key: string): void {
    this._storage.removeItem(this._getKey(key));
  }

  getKeys(): string[] {
    return this._storage
      .getKeys()
      .filter((key) => key.startsWith(annaPrefix))
      .map((key) => key.substring(annaPrefix.length));
  }
}
